import { InterfaceManager } from './manager.js'
import { BUILTIN_INTERFACES } from '../constants.js'

export interface AddInterfaceResult {
  success: boolean
  alias: string
  name: string
  newVersion?: string
  previousVersion?: string
  message?: string
}

export interface InterfaceOperationsOptions {
  projectPath: string
}

export class InterfaceOperations {
  private projectPath: string
  private manager: InterfaceManager

  constructor(options: InterfaceOperationsOptions) {
    this.projectPath = options.projectPath
    this.manager = new InterfaceManager()
  }

  async addInterface(_options: {
    name: string // may include workspace prefix (e.g., "ws/name")
    version?: string // optional, defaults to latest
    alias?: string
  }): Promise<AddInterfaceResult> {
    throw new Error(
      'Interfaces are built-in constants and cannot be added. The following interfaces are always available: ' +
        Object.keys(BUILTIN_INTERFACES).join(', ')
    )
  }

  async updateInterface(_options: {
    alias: string
    name: string // may include workspace prefix
    version: string // desired version (or 'latest')
    previousVersion: string
  }): Promise<AddInterfaceResult> {
    throw new Error(
      'Interfaces are built-in constants and cannot be updated. The following interfaces are always available: ' +
        Object.keys(BUILTIN_INTERFACES).join(', ')
    )
  }

  async removeInterface(_alias: string): Promise<boolean> {
    throw new Error(
      'Interfaces are built-in constants and cannot be removed. The following interfaces are always available: ' +
        Object.keys(BUILTIN_INTERFACES).join(', ')
    )
  }

  async listInterfaces(): Promise<
    Array<{
      alias: string
      name: string
      version: string
    }>
  > {
    return Object.entries(BUILTIN_INTERFACES).map(([alias, versionString]) => {
      const [name, version] = versionString.split('@')
      return { alias, name: name || '', version: version || 'latest' }
    })
  }

  /**
   * Get latest version info for an interface by name
   */
  async getInterfaceLatestVersion(name: string): Promise<{ name: string; latestVersion: string }> {
    // Resolve latest via manager and return version
    const latest = await this.manager.getByDependencyString(`${name}@latest`)
    return { name, latestVersion: latest.version }
  }

  /**
   * Upgrade interface(s) to latest version
   */
  async upgradeInterface(_alias?: string): Promise<{ upgraded: string[]; failed: string[] }> {
    throw new Error(
      'Interfaces are built-in constants and cannot be upgraded. The following interfaces are always available: ' +
        Object.keys(BUILTIN_INTERFACES).join(', ')
    )
  }
}
